import { type CSSResultGroup, LitElement, type PropertyValues, unsafeCSS } from 'lit'
import { html, unsafeStatic } from 'lit/static-html.js'
import { customElement, property, query } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { ContextConsumer } from '@lit/context'
import { type NavbarPart, navbarContext } from '@/navbar/components/context'
import type { Icon } from '@/navbar/navbar.types'
import { logWarn } from '@/utils/errorReporter'
import { emit } from '@/navbar/utils/emit'

// Styles:
import styles from './eco-suite-navbar-button.scss?inline'

// Child components:
import '../tooltip/eco-suite-navbar-tooltip.component'

/**
 * @summary A navbar button that can render as a button or anchor, with optional icon, label, tooltip, and badge.
 * @tag eco-suite-navbar-button
 * @slot icon - Custom icon to render inside the button, replacing the default icon.
 * @event eco-click - Fired when the button is activated by mouse or keyboard. Not fired when the button is `disabled`.
 */
@customElement('eco-suite-navbar-button')
export class EcoSuiteNavbarButton extends LitElement {
  /** @internal */ static readonly shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true }
  /** @internal */ static readonly styles: CSSResultGroup = [unsafeCSS(styles)]
  /** @internal */ private navbarContextConsumer?: ContextConsumer<{ __context__: NavbarPart }, this>
  /** @internal */ private hasWarnedAboutMissingName = false

  /** @internal */
  @query('.EcoSuiteNavbarButton')
  private readonly control!: HTMLElement

  /** Button icon. */
  @property()
  icon?: Icon

  /** Button label. */
  @property()
  label?: string

  /** Tooltip text to use if the label alone is not sufficient. */
  @property()
  tooltip?: string

  /** URL to link to if the button should act as a link. */
  @property()
  href?: string

  /** Button link target. */
  @property()
  target?: '_blank' | '_self'

  /**
   * Button click handler. Invoked alongside the `eco-click` event.
   */
  @property({ attribute: false })
  onClick?: (e: MouseEvent) => void

  /** Display the label regardless of screen size. */
  @property({ type: Boolean, attribute: 'show-label' })
  showLabel = false

  /** Display a green dot to the top-right of the button icon. */
  @property({ type: Boolean, attribute: 'has-badge' })
  hasBadge = false

  /**
   * Disables the button. When `href` is set, the button will be
   * rendered as a `<button>` instead of an anchor so it cannot be activated by mouse or keyboard.
   */
  @property({ type: Boolean })
  disabled = false

  /** @internal */
  @property({ type: Boolean, attribute: false })
  hasMenu = false

  /** @internal */
  @property({ type: Boolean, attribute: false })
  isActive = false

  /** @internal */
  @property({ type: Boolean, attribute: false })
  isAccountMenu = false

  /**
   * @internal
   * Override the inherited navbar context to force the button to appear with certain styles.
   * */
  @property({ attribute: false })
  forceNavbarContext?: NavbarPart

  /** @internal */
  get tag() {
    return unsafeStatic((this.href && !this.disabled) ? 'a' : 'button')
  }

  /** @internal */
  get tooltipIcon(): Icon | undefined {
    if (this.href && !this.disabled) return this.target === '_blank' ? 'external-link' : 'chevron-right'
    return undefined
  }

  /** @internal */
  get navbarContext() {
    return this.forceNavbarContext ?? this.navbarContextConsumer?.value
  }

  async connectedCallback(): Promise<void> {
    super.connectedCallback()
    await this.updateComplete

    this.navbarContextConsumer = new ContextConsumer(this, {
      context: navbarContext,
      subscribe: true,
    })

    if (!(this.label || this.tooltip)) this.warnMissingName()
  }

  protected updated(changed: PropertyValues<this>) {
    super.updated(changed)
    if (this.hasWarnedAboutMissingName) return

    if (changed.has('label') || changed.has('tooltip')) {
      if (!(this.label || this.tooltip)) this.warnMissingName()
    }
  }

  /** @internal */
  private warnMissingName() {
    if (this.hasWarnedAboutMissingName) return

    this.hasWarnedAboutMissingName = true
    logWarn(
      new Error('eco-suite-navbar-button: button is missing an accessible name. Provide a `label` and/or `tooltip`.'),
      { source: 'EcoSuiteNavbarButton' },
    )
  }

  /** Trigger a click on the inner button/link */
  public click() {
    this.control.click()
  }

  /** @internal */
  private readonly handleClick = (e: MouseEvent) => {
    if (this.disabled) return
    this.onClick?.(e)

    if (!e.defaultPrevented) {
      emit(this, 'eco-click')
    }
  }

  render() {
    const isExternalTab = this.target === '_blank'

    return html`
      <eco-suite-navbar-tooltip content=${ifDefined(this.tooltip ?? this.label)} .icon=${this.tooltipIcon}>
        <${this.tag}
          class=${classMap({
            EcoSuiteNavbarButton: true,
            'EcoSuiteNavbarButton--badge': !!this.hasBadge,
            'EcoSuiteNavbarButton--showLabel': !!this.showLabel && this.navbarContext === 'desktop',
            'EcoSuiteNavbarButton--hasMenu': !!this.hasMenu,
            'EcoSuiteNavbarButton--open': !!this.isActive && !this.isAccountMenu,
            'EcoSuiteNavbarButton--desktopAccountMenu': !!this.isAccountMenu && this.navbarContext === 'desktop',
            'EcoSuiteNavbarButton--mobileAccountMenu': !!this.isAccountMenu && this.navbarContext === 'mobile',
            'EcoSuiteNavbarButton--mobileMenu': this.navbarContext === 'mobile',
          })}
          href=${ifDefined((this.href && !this.disabled) ? this.href : undefined)}
          target=${ifDefined(this.target)}
          rel=${ifDefined(isExternalTab ? 'noopener noreferrer' : undefined)}
          aria-label=${ifDefined(this.tooltip ?? this.label)}
          aria-expanded=${ifDefined(this.hasMenu ? String(!!this.isActive) : undefined)}
          ?disabled=${this.disabled}
          @click=${this.handleClick}
        >
          <slot name="icon">
            <pwc-icon-shadow
              icon=${ifDefined(this.icon)}
              size="sm"
              aria-hidden="true"
            ></pwc-icon-shadow>
          </slot>
          <div class="EcoSuiteNavbarButton__label">
            <span>${this.label}</span>
          </div>
          ${
            this.hasMenu && (this.showLabel || this.navbarContext === 'mobile')
              ? html`
                <pwc-icon-shadow
                  icon="chevron-right"
                  size=${this.navbarContext === 'mobile' ? 'sm' : 'xs'}
                  aria-hidden="true"
                ></pwc-icon-shadow>`
              : ''
          }
        </${this.tag}>
      </eco-suite-navbar-tooltip>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'eco-suite-navbar-button': EcoSuiteNavbarButton
  }
}
